import { ENDING_TYPE_LABEL } from '@chess/api-ending-case';
import type { Phase } from '@chess/api-model';
import type { LastGame } from './LastGameAggregator.ts';

// The label after a stage section's heading (2026-10-10): what the stage was
// in this game and the moves it spanned. The opening names its family, then
// its variation (the part of the book's "Family: Variation" after the family,
// left out on the family's main line); the ending its classification, muted
// as a detail; the middlegame only its moves. A missing name is left out.

/** The moves a stage's curve spans: "moves 4–29", or "move 12" for one move. */
export function stageMoves(curve: { fromPly: number; toPly: number }): string {
  const first = Math.ceil((curve.fromPly + 1) / 2);
  const last = Math.max(first, Math.ceil(curve.toPly / 2));
  return first === last ? `move ${first}` : `moves ${first}–${last}`;
}

/** The label's parts: the stage's name in this game (the opening's family, in the ink colour), its detail (the opening's variation or the ending's classification, muted), its moves (muted). */
export interface StageLabel {
  name?: string;
  detail?: string;
  moves: string;
}

/** The variation after "Family: ", or undefined on the family's main line. */
function variationOf(variation: string | undefined): string | undefined {
  const i = variation?.indexOf(':') ?? -1;
  const rest = i < 0 ? '' : variation!.slice(i + 1).trim();
  return rest || undefined;
}

export function stageLabel(stage: Phase, game: Pick<LastGame, 'opening' | 'variation' | 'curve' | 'ending'>): StageLabel {
  const moves = stageMoves(game.curve);
  if (stage === 'opening') {
    const family = game.opening ?? game.variation?.split(':')[0]?.trim();
    const detail = variationOf(game.variation);
    return { ...(family ? { name: family } : {}), ...(detail && detail !== family ? { detail } : {}), moves };
  }
  // The ending's classification is one name, muted as before: a detail.
  const detail = stage === 'ending' && game.ending?.type ? ENDING_TYPE_LABEL[game.ending.type] : undefined;
  return { ...(detail ? { detail } : {}), moves };
}
